// @ts-strict-ignore
import MDEditor, { MDEditorProps } from '@uiw/react-md-editor';
import Mentions from 'antd/lib/mentions';
import type { MentionsRef } from 'antd/lib/mentions';
import * as React from 'react';
import rehypeSanitize from 'rehype-sanitize';
import { AllPatternTokens, OrgUser, PatternToken, UserGroup, UserGroupId } from '../../types';
import { displayUserNameLong, displayTextForUserChecklistGroup } from '../../helpers/displayHelpers';

// --- Constants ---

const MENTION_TOKEN_REGEX = /@\{(user|group):([^|}]*)\|([^}]*)\}/g;
const BOUNDARY_REGEX = /[\s,.:;!?\n]/;
const MENTION_PREFIXES: string[] = ['@'];
const PATTERN_PREFIXES: string[] = ['{'];
const BOTH_PREFIXES: string[] = ['@', '{'];
const PREVIEW_OPTIONS = { rehypePlugins: [rehypeSanitize] };

// Only way to override @uiw/react-md-editor's styling
const MENTIONS_FILL_CSS = `
.w-md-editor--mentions .w-md-editor-area {
    overflow: hidden !important;
}
.w-md-editor--mentions .w-md-editor-text {
    padding: 0 !important;
    height: 100% !important;
}
.w-md-editor--mentions .w-md-editor-text .ant-mentions {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
    border-radius: 0 !important;
}
.w-md-editor--mentions .w-md-editor-text .ant-mentions > *,
.w-md-editor--mentions .w-md-editor-text .ant-mentions .rc-mentions,
.w-md-editor--mentions .w-md-editor-text .ant-mentions .rc-textarea,
.w-md-editor--mentions .w-md-editor-text .ant-mentions .rc-textarea > textarea {
    height: 100% !important;
    width: 100% !important;
}
.w-md-editor--mentions .w-md-editor-text .ant-mentions,
.w-md-editor--mentions .w-md-editor-text .ant-mentions textarea,
.w-md-editor--mentions .w-md-editor-text .w-md-editor-text-input {
    -webkit-text-fill-color: initial !important;
    color: var(--color-fg-default, inherit) !important;
}
.w-md-editor--mentions .w-md-editor-text .ant-mentions textarea {
    resize: none !important;
    padding: 10px !important;
    box-shadow: none !important;
    overflow: auto !important;
}
`;

// --- Types ---

interface MentionMapping {
    displayName: string;
    token: string;
}

interface MentionTextareaProps {
    users?: OrgUser[];
    userGroups?: Record<UserGroupId, UserGroup>;
    availablePatternTokens?: PatternToken[];
    value?: string;
    onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
}

interface MarkdownEditorProps extends MDEditorProps {
    allowMentions?: boolean;
    users?: OrgUser[];
    userGroups?: Record<UserGroupId, UserGroup>;
    allowPatternedStrings?: boolean;
    availablePatternTokens?: PatternToken[];
    disabled?: boolean;
}

// --- Helpers ---

function decodeHTML(str: string) {
    if (!str) return '';

    // Decode HTML entities (e.g. &amp; -> &) without stripping the content. A <textarea>'s contents
    // are parsed as raw text (RCDATA), so literal angle-bracket text like "<your reason here>" is
    // preserved rather than being interpreted as an HTML tag (which DOMParser would drop).
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
}

function buildMentionMappings(
    users: OrgUser[] | undefined,
    userGroups: Record<UserGroupId, UserGroup> | undefined,
): MentionMapping[] {
    const mappings: MentionMapping[] = [];
    for (const u of users || []) {
        const display = displayUserNameLong(u);
        mappings.push({
            displayName: display,
            token: `@{user:${u.email}|${display}}`,
        });
    }
    for (const g of Object.values(userGroups || {})) {
        const display = displayTextForUserChecklistGroup(g);
        mappings.push({
            displayName: display,
            token: `@{group:${g.id}|${display}}`,
        });
    }
    return mappings;
}

function tokensToDisplay(text: string): string {
    return text.replace(MENTION_TOKEN_REGEX, (_match, _kind, _id, displayName) => `@${displayName}`);
}

function displayToTokens(text: string, mappings: MentionMapping[]): string {
    let result = text;
    const sorted = [...mappings].sort((a, b) => b.displayName.length - a.displayName.length);
    for (const m of sorted) {
        const needle = `@${m.displayName}`;
        let idx = result.indexOf(needle);
        while (idx !== -1) {
            const before = idx > 0 ? result[idx - 1] : ' ';
            const after = idx + needle.length < result.length ? result[idx + needle.length] : ' ';
            const isBoundary = BOUNDARY_REGEX.test(before) || idx === 0;
            const isEndBoundary = BOUNDARY_REGEX.test(after) || idx + needle.length === result.length;
            if (isBoundary && isEndBoundary) {
                result = result.slice(0, idx) + m.token + result.slice(idx + needle.length);
                idx = result.indexOf(needle, idx + m.token.length);
            } else {
                idx = result.indexOf(needle, idx + needle.length);
            }
        }
    }
    return result;
}

function buildPatternTokenOptions(availablePatternTokens?: PatternToken[]) {
    const tokens = availablePatternTokens ?? AllPatternTokens;
    return tokens.map((t) => {
        const inner = t.replace(/^\{/, '').replace(/\}$/, '');
        return { value: `${inner}}`, label: t };
    });
}

// --- Components ---

const MentionTextarea = React.forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
    ({ users, userGroups, availablePatternTokens, onChange, className, value, disabled }, ref) => {
        const mentionsRef = React.useRef<MentionsRef>(null);

        React.useImperativeHandle(ref, () => mentionsRef.current?.textarea as HTMLTextAreaElement, []);

        const mappings = React.useMemo(() => buildMentionMappings(users, userGroups), [users, userGroups]);

        const mentionOptions = React.useMemo(
            () =>
                mappings.map((m) => ({
                    value: m.displayName,
                    label: m.displayName,
                })),
            [mappings],
        );

        const patternTokenOptions = React.useMemo(
            () => buildPatternTokenOptions(availablePatternTokens),
            [availablePatternTokens],
        );

        const handleChange = React.useCallback(
            (text: string) => {
                if (onChange) {
                    onChange({ target: { value: text } } as React.ChangeEvent<HTMLTextAreaElement>);
                }
            },
            [onChange],
        );

        const hasMentionOptions = mentionOptions.length > 0;
        const hasPatternTokenOptions = patternTokenOptions.length > 0;

        const [activePrefix, setActivePrefix] = React.useState<string>('@');

        const handleSearch = React.useCallback((_text: string, prefix: string) => {
            setActivePrefix(prefix);
        }, []);

        const filteredOptions = React.useMemo(() => {
            if (activePrefix === '@') return mentionOptions;
            if (activePrefix === '{') return patternTokenOptions;
            return [];
        }, [activePrefix, mentionOptions, patternTokenOptions]);

        if (!hasMentionOptions && !hasPatternTokenOptions) {
            return (
                <textarea ref={ref} className={className} value={value ?? ''} disabled={disabled} onChange={onChange} />
            );
        }

        const prefixes =
            hasMentionOptions && hasPatternTokenOptions ? BOTH_PREFIXES
            : hasMentionOptions ? MENTION_PREFIXES
            : PATTERN_PREFIXES;

        return (
            <Mentions
                ref={mentionsRef}
                className={className}
                variant="borderless"
                value={value ?? ''}
                disabled={disabled}
                prefix={prefixes}
                options={filteredOptions}
                onChange={handleChange}
                onSearch={handleSearch}
                autoSize={false}
                allowClear={false}
            />
        );
    },
);

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
    allowMentions,
    users,
    userGroups,
    allowPatternedStrings,
    availablePatternTokens,
    disabled,
    ...props
}) => {
    const hasMentions =
        allowMentions && ((users && users.length > 0) || (userGroups && Object.keys(userGroups).length > 0));
    const hasPatternedStrings = allowPatternedStrings === true;
    const useCustomTextarea = hasMentions || hasPatternedStrings;

    const mentionUsers = hasMentions ? users : undefined;
    const mentionUserGroups = hasMentions ? userGroups : undefined;
    const mentionPatternTokens = hasPatternedStrings ? availablePatternTokens : undefined;

    const mappings = React.useMemo(
        () => buildMentionMappings(mentionUsers, mentionUserGroups),
        [mentionUsers, mentionUserGroups],
    );

    const displayValue = React.useMemo(() => {
        const decoded = decodeHTML(props.value);
        return hasMentions ? tokensToDisplay(decoded) : decoded;
    }, [props.value, hasMentions]);

    const handleChange = React.useCallback(
        (value?: string, event?: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (!props.onChange) return;
            const tokenized = hasMentions ? displayToTokens(value ?? '', mappings) : value;
            props.onChange(tokenized, event);
        },
        [props.onChange, hasMentions, mappings],
    );

    const renderTextarea = React.useCallback(
        (textareaProps: any) => (
            <MentionTextarea
                {...textareaProps}
                users={mentionUsers}
                userGroups={mentionUserGroups}
                availablePatternTokens={mentionPatternTokens}
                disabled={disabled}
            />
        ),
        [mentionUsers, mentionUserGroups, mentionPatternTokens, disabled],
    );

    return (
        <>
            {useCustomTextarea && <style>{MENTIONS_FILL_CSS}</style>}
            <MDEditor
                {...props}
                className={useCustomTextarea ? `w-md-editor--mentions ${props.className || ''}` : props.className}
                value={displayValue}
                onChange={handleChange}
                previewOptions={PREVIEW_OPTIONS}
                {...(useCustomTextarea ? { components: { textarea: renderTextarea } } : {})}
            />
        </>
    );
};

export default MarkdownEditor;
